import { typographyTokens } from '../../../../theme/typographyTokens';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import CircularProgress from '@mui/material/CircularProgress';
import Autocomplete from '@mui/material/Autocomplete';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import { debounce } from 'lodash';
import {
    useGetLogicAppSettingsQuery,
    useUpdateLogicAppSettingMutation,
} from '../../../../../frontend/services/logicAppSettingsSlice/logicAppSettingsSlice';
import { useSearchUsersQuery } from '../../../../../frontend/services/usersSlice';
import { LogicAppSetting } from '../../../../../shared/types/LogicAppSettings';
import { User } from '../../../../../shared/types/User';

interface LogicAppRow {
    id: number;
    logicAppName: string;
    description: string;
    ccEmail: string;
}

// Display metadata keyed by logic_app_key (matches DB seed values)
/* eslint-disable camelcase */
const LOGIC_APP_DISPLAY: Record<string, { name: string; description: string }> = {
    sep_expiry: {
        name: 'SEP Expiry Notification',
        description: 'CC email used for 30/60/85-day SEP expiry notifications',
    },
    sep_escalation: {
        name: 'SEP Auto-Escalation',
        description: 'CC email used for auto-escalation notifications to directors',
    },
};
/* eslint-enable camelcase */

const StyledTableCell = styled(TableCell)(({ theme }) => ({
    [`&.${tableCellClasses.head}`]: {
        backgroundColor: theme.palette.common.black,
        color: theme.palette.common.white,
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    [`&.${tableCellClasses.body}`]: {
        fontSize: typographyTokens.fontSizes.body,
    },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
    '&:nth-of-type(odd)': {
        backgroundColor: theme.palette.action.hover,
    },
    '&:last-child td, &:last-child th': {
        border: 0,
    },
}));

// Mock initial data — will be replaced by API call to DB later
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const parseEmails = (value: string): string[] =>
    value
        .split(/[;,\s]+/)
        .map((email) => email.trim())
        .filter(Boolean);

const normalizeEmails = (value: string): string => parseEmails(value).join('; ');

const appendEmails = (current: string, email: string): string => {
    const emails = parseEmails(current);
    const existing = new Set(emails.map((e) => e.toLowerCase()));
    const normalized = email.trim();
    
    if (!normalized || existing.has(normalized.toLowerCase())) {
        return current;
    }
    
    emails.push(normalized);
    return emails.join('; ');
};

const LogicAppCCEmails: React.FC = () => {
    const { data: settings, isLoading, isError } = useGetLogicAppSettingsQuery();
    const [updateLogicAppSetting, { isLoading: isSaving }] = useUpdateLogicAppSettingMutation();

    const [editingRowId, setEditingRowId] = useState<number | null>(null);
    const [editedValue, setEditedValue] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [userSearchTerm, setUserSearchTerm] = useState<string>('');
    const [userOptions, setUserOptions] = useState<User[]>([]);

    const debouncedSearchRef = useRef<ReturnType<typeof debounce>>(
        debounce((term: string) => {
            if (term.length >= 2) {
                // Query will be triggered automatically by useSearchUsersQuery
            }
        }, 300)
    );

    const { data: searchResults = [] } = useSearchUsersQuery(
        { search: userSearchTerm },
        { skip: userSearchTerm.length < 2 }
    );

    useEffect(() => {
        setUserOptions(searchResults);
    }, [searchResults]);

    useEffect(() => {
        const debounceRef = debouncedSearchRef.current;
        debounceRef(userSearchTerm);
        return () => debounceRef.cancel();
    }, [userSearchTerm]);

    // Map DB rows -> display rows (only show 'cc_email' settings)
    const rows: LogicAppRow[] = useMemo(() => {
        if (!settings) return [];
        return settings
            .filter((s: LogicAppSetting) => s.setting_key === 'cc_email')
            .map((s: LogicAppSetting) => {
                const meta = LOGIC_APP_DISPLAY[s.logic_app_key] || {
                    name: s.logic_app_key,
                    description: '',
                };
                return {
                    id: s.id,
                    logicAppName: meta.name,
                    description: meta.description,
                    ccEmail: s.setting_value ?? '',
                };
            });
    }, [settings]);

    const handleEditClick = (row: LogicAppRow) => {
        setEditingRowId(row.id);
        setEditedValue(row.ccEmail);
        setEmailError('');
    };

    const handleCancel = () => {
        setEditingRowId(null);
        setEditedValue('');
        setEmailError('');
        setUserSearchTerm('');
    };

    const validateEmails = (value: string): string => {
        if (!value.trim()) return '';
        const emails = parseEmails(value);
        const invalid = emails.filter((e) => !EMAIL_REGEX.test(e));
        if (invalid.length > 0) {
            return `Invalid email(s): ${invalid.join(', ')}`;
        }
        return '';
    };

    const handleSave = async (rowId: number) => {
        const error = validateEmails(editedValue);
        if (error) {
            setEmailError(error);
            return;
        }
        try {
            const normalizedValue = normalizeEmails(editedValue);
            await updateLogicAppSetting({
                id: rowId,
                // eslint-disable-next-line camelcase
                body: { setting_value: normalizedValue },
            }).unwrap();
            setEditingRowId(null);
            setEditedValue('');
            setEmailError('');
            setUserSearchTerm('');
        } catch {
            // snackbar handled by setSnackbarForEndpoint in the slice
        }
    };

    const handleValueChange = (value: string) => {
        setEditedValue(value);
        setEmailError(validateEmails(value));
    };

    const handleUserSelect = (user: User | null) => {
        if (!user?.email) return;
        const nextValue = appendEmails(editedValue, user.email);
        setEditedValue(nextValue);
        setEmailError(validateEmails(nextValue));
        setUserSearchTerm('');
    };

    const removeEmail = (emailToRemove: string) => {
        const emails = parseEmails(editedValue);
        const filtered = emails.filter((e) => e.toLowerCase() !== emailToRemove.toLowerCase());
        const nextValue = filtered.join('; ');
        setEditedValue(nextValue);
        setEmailError(validateEmails(nextValue));
    };

    const currentEmails = useMemo(() => parseEmails(editedValue), [editedValue]);
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (isError) {
        return (
            <Box sx={{ p: 2 }}>
                <Typography color="error">
                    Failed to load Logic App settings. Please try again later.
                </Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Typography sx={{ mb: 2, fontSize: typographyTokens.fontSizes.body, color: 'text.secondary' }}>
                Configure CC email addresses for Logic App notifications. Search and select users from Azure AD or enter emails manually. Use comma, semicolon, space, or newline to separate multiple emails.
            </Typography>
            <TableContainer component={Paper}>
                <Table sx={{ minWidth: 700 }} aria-label="logic app cc emails table">
                    <TableHead>
                        <TableRow>
                            <StyledTableCell>Logic App</StyledTableCell>
                            <StyledTableCell>Description</StyledTableCell>
                            <StyledTableCell>CC Email</StyledTableCell>
                            <StyledTableCell align="center">Actions</StyledTableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.length === 0 ? (
                            <StyledTableRow>
                                <StyledTableCell colSpan={4} align="center">
                                    No Logic App settings configured.
                                </StyledTableCell>
                            </StyledTableRow>
                        ) : (
                            rows.map((row) => {
                                const isEditing = editingRowId === row.id;
                                return (
                                    <StyledTableRow key={row.id}>
                                        <StyledTableCell sx={{ fontWeight: typographyTokens.fontWeights.semibold }}>
                                            {row.logicAppName}
                                        </StyledTableCell>
                                        <StyledTableCell>{row.description}</StyledTableCell>
                                        <StyledTableCell sx={{ minWidth: 380 }}>
                                            {isEditing ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {currentEmails.length > 0 && (
                                                        <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                                                            {currentEmails.map((email) => (
                                                                <Chip
                                                                    key={email}
                                                                    label={email}
                                                                    onDelete={() => removeEmail(email)}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    disabled={isSaving}
                                                                />
                                                            ))}
                                                        </Stack>
                                                    )}
                                                    <Autocomplete
                                                        options={userOptions.filter((u) => !!u.email)}
                                                        getOptionLabel={(option) => option.displayName || option.email || ''}
                                                        isOptionEqualToValue={(option, value) => option.id === value.id}
                                                        value={null}
                                                        inputValue={userSearchTerm}
                                                        onInputChange={(_event, value) => setUserSearchTerm(value)}
                                                        onChange={(_event, selectedUser) => handleUserSelect(selectedUser)}
                                                        filterOptions={(options) => options}
                                                        noOptionsText={userSearchTerm.length < 2 ? 'Type at least 2 characters' : 'No users found'}
                                                        renderOption={(props, option) => (
                                                            <li {...props}>
                                                                <Typography sx={{ fontSize: typographyTokens.fontSizes.sm, fontWeight: typographyTokens.fontWeights.semibold }}>
                                                                    {option.displayName}
                                                                </Typography>
                                                                <Typography sx={{ fontSize: typographyTokens.fontSizes.caption, color: 'text.secondary' }}>
                                                                    {option.email}
                                                                </Typography>
                                                            </li>
                                                        )}
                                                        renderInput={(params) => (
                                                            <TextField
                                                                {...params}
                                                                size="small"
                                                                placeholder="Search Azure AD users or type/paste emails"
                                                                helperText={emailError || (currentEmails.length > 0 ? 'Click chip to remove' : '')}
                                                                error={!!emailError}
                                                                disabled={isSaving}
                                                            />
                                                        )}
                                                        disabled={isSaving}
                                                    />
                                                </Box>
                                            ) : (
                                                <Typography sx={{ fontSize: typographyTokens.fontSizes.body }}>
                                                    {row.ccEmail || (
                                                        <em style={{ color: '#888' }}>Not set</em>
                                                    )}
                                                </Typography>
                                            )}
                                        </StyledTableCell>
                                        <StyledTableCell align="center">
                                            {isEditing ? (
                                                <>
                                                    <Tooltip title="Save">
                                                        <span>
                                                            <IconButton
                                                                color="primary"
                                                                onClick={() => handleSave(row.id)}
                                                                disabled={!!emailError || isSaving}
                                                            >
                                                                {isSaving ? (
                                                                    <CircularProgress size={20} />
                                                                ) : (
                                                                    <SaveIcon />
                                                                )}
                                                            </IconButton>
                                                        </span>
                                                    </Tooltip>
                                                    <Tooltip title="Cancel">
                                                        <IconButton
                                                            onClick={handleCancel}
                                                            disabled={isSaving}
                                                        >
                                                            <CancelIcon />
                                                        </IconButton>
                                                    </Tooltip>
                                                </>
                                            ) : (
                                                <Tooltip title="Edit CC Email">
                                                    <IconButton
                                                        color="primary"
                                                        onClick={() => handleEditClick(row)}
                                                    >
                                                        <EditIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            )}
                                        </StyledTableCell>
                                    </StyledTableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default LogicAppCCEmails;
